import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Library, Calendar, RefreshCw } from 'lucide-react';
import { Set } from '../../types/mtg';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useCachedSetsCardCounts } from '../../hooks/useCachedSetsCardCounts';
import { useI18n } from '../../contexts/I18nContext';

// Table de correspondance entre les codes anglais et français des sets si nécessaire
const SET_NAME_MAPPING: Record<string, string> = {
  'genetic-apex': 'puissance-genetique',
  'promo-a': 'promo-a',
  'mythical-island': 'l-ile-fabuleuse',
  'space-time-smackdown': 'choc-spatio-temporel',
  'triumphant-light': 'lumiere-triomphale',
  'shining-revelry': 'rejouissances-rayonnantes'
};

interface SetGridProps {
  sets: Set[];
  loading?: boolean;
}

export function SetGrid({ sets, loading }: SetGridProps) {
  const { t, lang, getPathForLang } = useI18n();
  
  // Utiliser le hook optimisé qui récupère toutes les statistiques en une seule requête
  const { 
    cardCounts, 
    collectedCounts, 
    loading: statsLoading, 
    refreshCollectionCounts 
  } = useCachedSetsCardCounts();

  // Fonction de debug pour afficher les données dans la console
  useEffect(() => {
    if (!statsLoading && Object.keys(cardCounts).length > 0) {
      console.log('Détails des sets:');
      sets.forEach(set => {
        console.log(`Set ${set.name || set.id}:`, {
          id: set.id,
          name: set.name,
          displayName: set.display_name,
          totalCards: cardCounts[set.name] || 0,
          collectedCards: collectedCounts[set.name] || 0
        });
      });
      console.log('Tous les comptages de cartes:', cardCounts);
      console.log('Tous les comptages de collection:', collectedCounts);
    }
  }, [statsLoading, cardCounts, collectedCounts, sets]);

  // Fonction pour obtenir le nom du set en fonction de la langue
  const getSetName = (set: Set) => {
    return lang === 'en' ? (set.display_name_en || set.display_name) : set.display_name;
  };

  const handleRefresh = async () => {
    await refreshCollectionCounts();
  };

  if (loading || statsLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-xl p-6 shadow-md animate-pulse"
          >
            <div className="w-16 h-16 bg-gray-200 rounded-lg mb-4" />
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-700">{t('sets.collection_stats')}</h2>
        <button 
          onClick={handleRefresh}
          className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-md text-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          {t('actions.refresh')}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {sets.map((set) => {
          // Récupérer les statistiques pour ce set
          const totalCards = cardCounts[set.name] || 0;
          const collectedCards = collectedCounts[set.name] || 0;
          const percentage = totalCards > 0 ? Math.round((collectedCards / totalCards) * 100) : 0;
        
          return (
            <Link
              key={set.id}
              to={getPathForLang(`/sets/${set.name}`)}
              className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow duration-200"
            >
              <div className="flex items-start space-x-4">
                {set.img ? (
                  <img
                    src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${set.img}`}
                    alt={`${getSetName(set)} ${t('set_detail.card_total', { count: totalCards })}`}
                    className="w-16 h-16 object-contain"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Library className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors duration-200">
                    {getSetName(set)}
                  </h3>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>{collectedCards} / {totalCards} {t('set_detail.collection.cards_in_collection')}</span>
                      <span>{percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );
}